import React, { useEffect } from "react";
import {
  dehydrate,
  hydrate,
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { retryLouiseQuery } from "./retry-policy";

const PERSIST_KEY = "@louise/query-cache";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: retryLouiseQuery,
      refetchOnWindowFocus: false,
    },
  },
});

// Restore persisted queries before first render
AsyncStorage.getItem(PERSIST_KEY)
  .then((raw) => {
    if (raw) hydrate(queryClient, JSON.parse(raw));
  })
  .catch(() => {});

function QueryCachePersister({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const unsubscribe = qc.getQueryCache().subscribe(() => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const state = dehydrate(qc, {
          shouldDehydrateQuery: (query) => query.meta?.persist === true,
        });
        AsyncStorage.setItem(PERSIST_KEY, JSON.stringify(state)).catch(
          () => {},
        );
      }, 1000);
    });

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [qc]);

  return children;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <QueryCachePersister>{children}</QueryCachePersister>
    </QueryClientProvider>
  );
}
